import { WIX_AI_CHAT_ASSISTANT, WIX_CHAT } from '@wix/app-definition-ids';
import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import {
  AppInstallOption,
  AppInstallOrigin,
  SilentInstallAppsCallbacks,
  SilentInstallAppsConfig,
  SilentInstallAppsOptions,
} from '@wix/editor-platform-host-integration-apis';
import { InstallationOriginType } from '@wix/editor-platform-sdk-types';
import { reportBi } from '../../essentials';
import { PlatformContext } from '../../types/platformApi';
import { appMarketService } from '../utils/appMarketService';
import { getAppType } from '../utils/appMarketUtils';
import { IExperimentsApiAdapter } from '../utils/experimentsApiAdapter';
import { getGUID } from '../utils/guid';
import { getPendingApps } from '../utils/pendingAppsService';
import { createDependenciesDriver } from './appDependenciesDriver';
import { installApps } from './installServiceEditor';
import {
  IntentInstallBIEvent,
  IntentInstallBIEventStep,
} from './intentInstallService/BIManager';
import { getEditorSubType, getIsImpersonated } from '../../utils';

let callbacks: SilentInstallAppsCallbacks;
let config: SilentInstallAppsConfig;
let appNames: Record<string, string>;
let silentFlag: boolean;
let flowId: string;
let appsWithErrors: string[];

interface CommonAppOptions {
  sourceTemplateId?: string;
  origin: AppInstallOrigin;
  isSilent: boolean;
}

const initDefaultCallbacks: () => SilentInstallAppsCallbacks = () => ({
  afterAppInstall: (): Promise<void> => Promise.resolve(),
  beforeAppInstall: (): Promise<void> => Promise.resolve(),
  onInstallationError: (): Promise<void> => Promise.resolve(),
  onInstallationBegin: (): Promise<void> => Promise.resolve(),
  onInstallationEnd: (): Promise<void> => Promise.resolve(),
});

const finishAllCallback =
  (appDefIds: string[], resolve: () => void) => async () => {
    setIsSilentFlag(false);
    await callbacks.onInstallationEnd(
      appsWithErrors,
      appDefIds,
      appNames,
      flowId,
    );
    resolve();
  };

const singleAppInstalled =
  (appDefIdsToSilentInstall: string[]) => async (installedAppDef: string) => {
    await callbacks.afterAppInstall(
      installedAppDef,
      appDefIdsToSilentInstall,
      appNames,
      flowId,
    );
  };

const setInitialState = () => {
  appNames = {};
  flowId = getGUID();
  appsWithErrors = [];
};

const isSilentInstallShouldHappen = (experimentsApi: IExperimentsApiAdapter) =>
  experimentsApi.enabled('se_removeSilentInstallRestrictions')
    ? Boolean(config?.shouldRestrictActions)
    : true;

function getCommonAppOptions(
  _experimentsAPI: IExperimentsApiAdapter,
  appDefIds: string[],
  options: SilentInstallAppsOptions,
): Record<string, AppInstallOption> {
  const { appsOptions } = options;
  return appDefIds.reduce((acc, appDefId) => {
    acc[appDefId] = {
      origin: config.platformOrigin,
      isSilent: true,
      pluginsAutoPopulate: {
        isSilent: true,
      },
      ...appsOptions?.[appDefId],
    };

    return acc;
  }, {} as Record<string, AppInstallOption>);
}

const isSilentInstallRunning = (): boolean => silentFlag;

const setIsSilentFlag = (val: boolean): void => {
  silentFlag = val;
};

const errorHandler =
  (appDefIds: string[], resolve: () => void, serialAppDefId?: string) =>
  async (err: Error, errName: string, appDefId: string = '') => {
    appsWithErrors.push(serialAppDefId || appDefId);
    if (!config.shouldInstallSerial) {
      setIsSilentFlag(false);
    }
    await callbacks.onInstallationError(
      err,
      errName,
      serialAppDefId || appDefId,
      appDefIds,
      appNames,
      flowId,
    );
    resolve();
  };

function install(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  appDefIds: string[],
  options: SilentInstallAppsOptions,
  allAppDefIds: string[],
  serialInstallation: boolean,
): Promise<void> {
  return new Promise((resolve) => {
    installApps(
      context,
      documentServicesAPI,
      appDefIds,
      {
        noForcedSaveAfterInstallationCompleted: true, // there is custom save at the end of silent install
        appsOptions: getCommonAppOptions(experimentsAPI, appDefIds, options),
      },
      {
        singleAppCallback: singleAppInstalled(allAppDefIds),
        finishAllCallback: serialInstallation
          ? () => resolve()
          : finishAllCallback(allAppDefIds, resolve),
        onError: errorHandler(
          allAppDefIds,
          resolve,
          serialInstallation ? appDefIds[0] : undefined,
        ),
      },
    );
  });
}

async function getAppDependencies(
  documentServicesAPI: DocumentServicesObject,
  appDefId: string,
  appDefIds: string[],
) {
  const DependenciesDriver = await createDependenciesDriver(
    documentServicesAPI,
    appDefId,
  );

  const { appsToInstall, dependencyOptions } =
    DependenciesDriver.getAppsToInstall();

  const dependenciesToInstall = appsToInstall.filter(
    (dependencyAppDefId) =>
      dependencyAppDefId !== appDefId &&
      !appDefIds.includes(dependencyAppDefId),
  );

  return { dependenciesToInstall, dependencyOptions };
}

async function installAppDependencies(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  appDefId: string,
  appDefIds: string[],
) {
  const DependenciesDriver = await createDependenciesDriver(
    documentServicesAPI,
    appDefId,
  );

  const { appsToInstall, dependencyOptions } =
    DependenciesDriver.getAppsToInstall();

  const dependenciesToInstall = appsToInstall.filter(
    (dependencyAppDefId) =>
      dependencyAppDefId !== appDefId &&
      !appDefIds.includes(dependencyAppDefId),
  );

  for (const dependencyAppDefId of dependenciesToInstall) {
    await install(
      context,
      documentServicesAPI,
      experimentsAPI,
      [dependencyAppDefId],
      { appsOptions: dependencyOptions },
      appDefIds,
      true,
    );
  }
}

const getAppOptionsToInstall = async (
  documentServicesAPI: DocumentServicesObject,
  _experimentsAPI: IExperimentsApiAdapter,
  appDefId: string,
  appDefIds: string[],
  options: SilentInstallAppsOptions,
) => {
  const { dependenciesToInstall, dependencyOptions } = await getAppDependencies(
    documentServicesAPI,
    appDefId,
    appDefIds,
  );

  const appsOptionsWithDependencies = {
    ...(dependencyOptions || {}),
    ...(options.appsOptions || {}),
  };

  return {
    appsToInstall: [...dependenciesToInstall, appDefId],
    options: {
      ...options,
      appsOptions: appsOptionsWithDependencies,
    },
  };
};

async function installAppsSerially(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  appDefIds: string[],
  originalOptions: SilentInstallAppsOptions,
): Promise<void> {
  for (const appDefId of appDefIds) {
    if (documentServicesAPI.platform.isAppActive(appDefId)) {
      continue;
    }
    await callbacks.beforeAppInstall(appDefId, appDefIds, appNames, flowId);

    const { appsToInstall, options } = await getAppOptionsToInstall(
      documentServicesAPI,
      experimentsAPI,
      appDefId,
      appDefIds,
      originalOptions,
    );

    await install(
      context,
      documentServicesAPI,
      experimentsAPI,
      appsToInstall,
      options,
      appDefIds,
      true,
    );
  }
  await finishAllCallback(appDefIds, () => {})();
}

const allowedWidgetApps = new Set([WIX_CHAT, WIX_AI_CHAT_ASSISTANT]);

const canInstallAppSilently = (appData: EditorClientSpecMapEntry) => {
  if (config.shouldInstallWidgets) {
    return true;
  }

  const type = getAppType(appData);
  return allowedWidgetApps.has(appData.appDefinitionId) || type !== 'tpaWidget';
};

function reportSilentInstallBegin(
  installationId: string,
  appDefinitionId: string[],
  step: IntentInstallBIEventStep,
) {
  return reportBi(IntentInstallBIEvent.INTENT_INSTALL_BEGIN, {
    installationId,
    appDefinitionId,
    origin: 'LEGACY_SILENT_INSTALL',
    editorType: getEditorSubType(),
    step,
  });
}

async function silentInstall(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  appsToInstallSilently: string[] = [],
  options: SilentInstallAppsOptions,
  serviceConfig: SilentInstallAppsConfig,
  serviceCallbacks: Partial<SilentInstallAppsCallbacks>,
): Promise<void> {
  const isImpersonated = getIsImpersonated();
  if (isImpersonated) {
    return;
  }
  const reportInstallationBI = (
    installationId: string,
    apps: string[],
    step: IntentInstallBIEventStep,
  ) => {
    if (
      serviceConfig.platformOrigin.info?.type !==
      InstallationOriginType.SILENT_INSTALL
    ) {
      // needed for capturing exposure vs intent install.
      // will we extended to more flows once they are integrated with intent install
      return;
    }

    reportSilentInstallBegin(installationId, apps, step);
  };

  if (isSilentInstallRunning()) {
    throw new Error('Silent install already in progress');
  }
  config = serviceConfig;
  callbacks = { ...initDefaultCallbacks(), ...serviceCallbacks };
  const shouldInstallPending = config.shouldInstallPending ?? true;
  const ignoredApps = config.ignoredApps ?? [];
  const pendingSectionApps = shouldInstallPending
    ? getPendingApps(documentServicesAPI, canInstallAppSilently)
    : [];
  const pendingAppDefs = pendingSectionApps.map((app) => app.appDefinitionId);
  const appsToInstall = [...appsToInstallSilently, ...pendingAppDefs];
  if (appsToInstall.length) {
    setIsSilentFlag(isSilentInstallShouldHappen(experimentsAPI));
  } else {
    reportInstallationBI(getGUID(), [], IntentInstallBIEventStep.CHECK_INSTALL);
    setIsSilentFlag(false);
    return;
  }
  setInitialState();

  const ignoredAppsSet = new Set(ignoredApps);
  const appDefIdsToSilentInstall = appsToInstall.filter(
    (appDefId) =>
      !ignoredAppsSet.has(appDefId) &&
      !documentServicesAPI.platform.isAppActive(appDefId),
  );
  reportInstallationBI(
    flowId,
    appDefIdsToSilentInstall,
    IntentInstallBIEventStep.CHECK_INSTALL,
  );

  if (!appDefIdsToSilentInstall.length) {
    setIsSilentFlag(false);
    return;
  }

  reportInstallationBI(
    flowId,
    appDefIdsToSilentInstall,
    IntentInstallBIEventStep.BEGIN_INSTALL,
  );

  try {
    appNames = await appMarketService.fetchAppsNames(appDefIdsToSilentInstall);

    await callbacks.onInstallationBegin(
      appDefIdsToSilentInstall,
      appNames,
      flowId,
    );

    if (config.shouldInstallSerial) {
      await installAppsSerially(
        context,
        documentServicesAPI,
        experimentsAPI,
        appDefIdsToSilentInstall,
        options,
      );
    } else {
      await install(
        context,
        documentServicesAPI,
        experimentsAPI,
        appDefIdsToSilentInstall,
        options,
        appDefIdsToSilentInstall,
        false,
      );
    }
  } catch (e) {
    setIsSilentFlag(false);
    throw e;
  }
}

export { isSilentInstallRunning, silentInstall };
